import { slot } from "./dom.js";
import { findScriptPanels, type ScriptPanel } from "./script-panels.js";

/**
 * Finding the parts of the page the script converter drives.
 *
 * Its own module for the reason `hanzi-elements.ts` is one: the shape of the
 * template is a separate concern from what the tool does with it, and the
 * answer is all-or-nothing. A template missing a slot yields nothing at all,
 * so the tool declines to start rather than half-working in a way nobody would
 * notice.
 */

/** Everything on the page this tool drives. */
export interface ScriptElements {
  readonly input: HTMLTextAreaElement;
  readonly status: HTMLElement;

  /** The output panels, one orthography each, in the template's order. */
  readonly panels: readonly ScriptPanel[];

  /**
   * The block holding the account of what had to be decided.
   *
   * Separate from the list because it is what gets hidden: the heading is only
   * meaningful with rows under it, and hiding the list alone would leave
   * "Characters with more than one form" standing over nothing.
   */
  readonly choicesPanel: HTMLElement | undefined;
  readonly choices: HTMLElement | undefined;
}

/**
 * Find the tool's parts, or nothing where the template is missing one.
 *
 * A page with no output panel on it is one of those: there is nowhere to put a
 * conversion, so there is no point running one.
 */
export function findScriptElements(
  container: HTMLElement,
): ScriptElements | undefined {
  const input = slot(container, "input");
  const status = slot(container, "status");
  const panels = findScriptPanels(container);

  if (
    !(input instanceof HTMLTextAreaElement) ||
    status === undefined ||
    panels.length === 0
  ) {
    return undefined;
  }

  return {
    input,
    status,
    panels,
    choicesPanel: slot(container, "choices-panel"),
    choices: slot(container, "choices"),
  };
}
